// trailhead/SessionSpendHud.tsx
import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import type { CouncilCost } from '../../types/council';
import { computeSpendDisplay } from './computeSpendDisplay';

// labelByTurn maps turn_message_id → human label (persona name) so the breach list
// never shows a raw UUID; falls back to a short id. Built by TrailheadHeader from
// state.messages + personasById (the header owns both).
export function SessionSpendHud(
  { cost, labelByTurn }: { cost?: CouncilCost | null; labelByTurn?: Map<string, string> },
) {
  const { darkMode } = useTheme();
  const [open, setOpen] = useState(false);
  const d = computeSpendDisplay(cost);
  if (!d) return null;
  const { frac, breaches, barColor } = d;
  const events = cost!.overshoot_events ?? [];
  const label = (e: { turn_message_id: string }) =>
    labelByTurn?.get(e.turn_message_id) ?? `turn ${e.turn_message_id.slice(0, 8)}`;

  return (
    <span className={`inline-flex flex-col text-[11px] ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
      <button type="button" data-testid="spend-hud-toggle" onClick={() => setOpen(o => !o)} className="inline-flex items-center gap-1.5">
        <span aria-hidden>💰</span>
        <span>${cost.cost_used_usd} / ${cost.cost_cap_usd_per_day}</span>
        <span className={`inline-block h-1.5 w-16 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
          <span className={`block h-1.5 rounded ${barColor}`} style={{ width: `${frac * 100}%` }} />
        </span>
        {/* "▲ N over" + the word disambiguates from the app's expand/collapse ▲; the chevron
            (not ▲) carries the toggle affordance so severity and toggle aren't double-encoded. */}
        {breaches > 0 && (
          <>
            <span className="font-bold text-red-600 dark:text-red-400" data-testid="spend-hud-breach-count">▲ {breaches} over</span>
            <span aria-hidden className="opacity-60">{open ? '▾' : '▸'}</span>
          </>
        )}
      </button>
      {open && breaches > 0 && (
        <span data-testid="spend-hud-breach-list" className="mt-1 flex flex-col gap-0.5">
          {events.map(e => (
            <span key={`${e.turn_message_id}|${e.scope}`} className="text-[10px] opacity-80">
              ▲ {label(e)} · over {e.scope} cap: used ${e.used_after} / cap ${e.scope_cap}
            </span>
          ))}
        </span>
      )}
    </span>
  );
}
